import axios from 'axios';

const API_URL = '';
const CSRF_COOKIE_NAME = 'prism_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const SESSION_FLAG_KEY = 'prism_session_active';

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function hasSession() {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
}

function markSession(active) {
  if (typeof window === 'undefined') return;
  if (active) window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
  else window.sessionStorage.removeItem(SESSION_FLAG_KEY);
}

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      const csrf = readCookie(CSRF_COOKIE_NAME);
      if (csrf) config.headers[CSRF_HEADER_NAME] = csrf;
    }
    config.headers['Cache-Control'] = 'no-cache';
    if (method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      markSession(false);
      const isLoginPage = typeof window !== 'undefined'
        && window.location.pathname.endsWith('/login');
      if (!isLoginPage && typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

function fetchHeaders(extra = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    ...extra,
  };
  const csrf = readCookie(CSRF_COOKIE_NAME);
  if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  return headers;
}

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    markSession(true);
    return response.data;
  },

  logout: async () => {
    try {
      const response = await api.post('/api/auth/logout');
      return response.data;
    } finally {
      markSession(false);
    }
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
};

export const conversationsAPI = {
  list: async () => {
    const response = await api.get('/api/conversations');
    return response.data;
  },

  create: async (title, initialMessage = null, tempNamespace = null, attachmentFilename = null) => {
    const response = await api.post('/api/conversations', {
      title,
      initial_message: initialMessage,
      temp_namespace: tempNamespace,
      attachment_filename: attachmentFilename,
    });
    return response.data;
  },

  initConversation: async (initialMessage = null, title = null, tempNamespace = null, attachmentFilename = null) => {
    const response = await api.post('/api/conversations/init', {
      title: title || null,
      initial_message: initialMessage || null,
      temp_namespace: tempNamespace || null,
      attachment_filename: attachmentFilename || null,
    });
    return response.data;
  },

  updateConversationTitle: async (conversationId, initialMessage) => {
    try {
      const response = await api.post(
        `/api/conversations/init/${conversationId}/update-title`,
        { initial_message: initialMessage, title: null },
      );
      return response.data;
    } catch (err) {
      console.warn('[Phase C] title update failed:', err?.message);
      return null;
    }
  },

  get: async (conversationId) => {
    const response = await api.get(`/api/conversations/${conversationId}`);
    return response.data;
  },

  update: async (conversationId, title) => {
    const response = await api.put(`/api/conversations/${conversationId}`, { title });
    return response.data;
  },

  delete: async (conversationId) => {
    await api.delete(`/api/conversations/${conversationId}`);
  },

  detachAttachment: async (conversationId) => {
    await api.delete(`/api/conversations/${conversationId}/attachment`);
  },

  sendMessage: async (conversationId, content) => {
    const response = await api.post(
      `/api/conversations/${conversationId}/messages`,
      { content },
    );
    return response.data;
  },

  sendMessageStream: (conversationId, content, callbacks = {}, responseMode = 'detailed') => {
    const { onSources, onToken, onTrimTo, onComplete, onError } = callbacks;
    const controller = new AbortController();

    const run = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/conversations/${conversationId}/messages/stream`,
          {
            method: 'POST',
            headers: fetchHeaders(),
            credentials: 'include',
            body: JSON.stringify({ content, response_mode: responseMode }),
            signal: controller.signal,
          },
        );

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          onError && onError(`Server error ${res.status}: ${text}`);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice('data: '.length).trim();
            if (!jsonStr) continue;

            let event;
            try { event = JSON.parse(jsonStr); } catch { continue; }

            if (event.type === 'sources') {
              onSources && onSources(event.sources || [], event.source_type);
            } else if (event.type === 'sources_complete') {
              onSources && onSources(event.sources || [], null);
            } else if (event.type === 'trim_to') {
              onTrimTo && onTrimTo(event.length);
            } else if (event.type === 'token') {
              onToken && onToken(event.token || '');
            } else if (event.type === 'complete') {
              if (event.sources && event.sources.length > 0) {
                onSources && onSources(event.sources, null);
              }
              onComplete && onComplete(event.message_id, event);
            } else if (event.type === 'error') {
              onError && onError(event.error || 'Unknown streaming error');
            }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          onError && onError(err.message || 'Stream connection failed');
        }
      }
    };

    run();
    return controller;
  },

  search: async (query) => {
    const response = await api.get(`/api/conversations/search/${encodeURIComponent(query)}`);
    return response.data;
  },

  getSignedUrl: async (filename) => {
    const response = await api.get(`/api/conversations/signed-url/${encodeURIComponent(filename)}`);
    return response.data;
  },

  sendMessageWithFile: async (conversationId, content, tempNamespace) => {
    const response = await api.post(
      `/api/conversations/${conversationId}/messages-with-file`,
      { content, temp_namespace: tempNamespace },
    );
    return response.data;
  },

  sendMessageWithFileStream: (
    conversationId,
    content,
    tempNamespace,
    filename = 'Attached Document',
    callbacks = {},
    responseMode = 'detailed',
  ) => {
    const { onSources, onToken, onTrimTo, onComplete, onError } = callbacks;
    const controller = new AbortController();

    const run = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/conversations/${conversationId}/messages-with-file/stream`,
          {
            method: 'POST',
            headers: fetchHeaders(),
            credentials: 'include',
            body: JSON.stringify({
              content,
              response_mode: responseMode,
              temp_namespace: tempNamespace,
              filename,
            }),
            signal: controller.signal,
          },
        );

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          onError && onError(`Server error ${res.status}: ${text}`);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice('data: '.length).trim();
            if (!jsonStr) continue;

            let event;
            try { event = JSON.parse(jsonStr); } catch { continue; }

            if (event.type === 'sources') {
              onSources && onSources(event.sources || [], event.source_type);
            } else if (event.type === 'sources_complete') {
              onSources && onSources(event.sources || [], null);
            } else if (event.type === 'trim_to') {
              onTrimTo && onTrimTo(event.length);
            } else if (event.type === 'token') {
              onToken && onToken(event.token || '');
            } else if (event.type === 'complete') {
              if (event.sources && event.sources.length > 0) {
                onSources && onSources(event.sources, null);
              }
              onComplete && onComplete(event.message_id, event);
            } else if (event.type === 'error') {
              onError && onError(event.error || 'Unknown streaming error');
            }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          onError && onError(err.message || 'Stream connection failed');
        }
      }
    };

    run();
    return controller;
  },
};

export const fileAPI = {
  upload: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  cleanup: async (namespace) => {
    const response = await api.delete(`/files/cleanup/${encodeURIComponent(namespace)}`);
    return response.data;
  },

  getSupportedTypes: async () => {
    const response = await api.get('/files/supported-types');
    return response.data;
  },
};

export const feedbackAPI = {
  create: async (feedback) => {
    const response = await api.post('/api/feedback', feedback);
    return response.data;
  },
};

export default api;
